/**
 * Profile → Stats: what your presence on Otable is doing for you, shown plainly so that a glance is
 * encouraging — reach this month, who you work with, your record, your writing and what is waiting for you.
 * Working together, the research record and the writing heatmap have an eye button: shared cards are what
 * other Otable users see on your profile (GET /users/<id>/stats/), and the tab appears for them when at least
 * one is shared. The reach cards (visits, followers, Writer saves) and "Waiting for you" stay personal.
 */
import React, { useState } from 'react';
import useSWR from 'swr';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, CircularProgress, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowUpward as UpIcon, ArrowDownward as DownIcon, Remove as SameIcon, ArrowForward as GoIcon,
  Visibility as SharedIcon, VisibilityOff as PrivateIcon,
} from '@mui/icons-material';
import { visuallyHidden } from '@mui/utils';
import { axiosService, fetcher } from 'shared/helpers/axios';
import WritingHeatmap from './WritingHeatmap';

const n = (v) => (v ?? 0).toLocaleString();

function Delta({ now, before }) {
  const diff = (now || 0) - (before || 0);
  const Icon = diff > 0 ? UpIcon : diff < 0 ? DownIcon : SameIcon;
  const text = diff === 0 ? 'same as the month before' : `${diff > 0 ? '+' : '−'}${Math.abs(diff)} on the month before`;
  return (
    <Box display="flex" alignItems="center" gap={0.5} sx={{ color: diff > 0 ? 'success.main' : 'text.secondary', mt: 0.5 }}>
      <Icon sx={{ fontSize: 15 }} aria-hidden />
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{text}</Typography>
    </Box>
  );
}

/** Eye button in a card's corner: is this card shown to other Otable users? */
export function ShareToggle({ shared, onToggle }) {
  if (!onToggle) return null;
  const label = shared ? 'Shown to Otable users on your profile. Click to keep it to yourself'
    : 'Only you see this. Click to show it to Otable users on your profile';
  return (
    <Tooltip title={label}>
      <IconButton size="small" aria-label={label} aria-pressed={shared} onClick={onToggle}
        sx={{ mt: -0.75, mr: -0.75, color: shared ? 'primary.main' : 'text.disabled' }}>
        {shared ? <SharedIcon sx={{ fontSize: 19 }} /> : <PrivateIcon sx={{ fontSize: 19 }} />}
      </IconButton>
    </Tooltip>
  );
}

function Tile({ label, value, children, action }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
      <Box display="flex" alignItems="flex-start">
        <Typography sx={{ fontSize: 13, color: 'text.secondary', flex: 1 }}>{label}</Typography>
        {action}
      </Box>
      <Typography sx={{ fontSize: 34, fontWeight: 700, lineHeight: 1.15, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {children}
    </Paper>
  );
}

function Card({ title, subtitle, children, action }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Box display="flex" alignItems="flex-start" gap={1} mb={subtitle ? 0.25 : 1.75}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>{title}</Typography>
        {action}
      </Box>
      {subtitle && <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 1.75 }}>{subtitle}</Typography>}
      {children}
    </Paper>
  );
}

/** Single-series column chart: columns of at most 24px with 4px rounded tops on one hairline baseline,
 *  a hover tooltip per column (the hit target is the whole slot) and a hidden table for screen readers. */
function Columns({ data, label, height = 120, color, axis }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <Box sx={{ position: 'relative' }}>
      <Box role="img" aria-label={label}
        sx={{ display: 'flex', alignItems: 'flex-end', height, borderBottom: '1px solid', borderColor: 'divider' }}>
        {data.map((d, i) => (
          <Box key={d.key} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
            sx={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', cursor: 'default' }}>
            {d.value > 0 && (
              <Box sx={{ width: '62%', maxWidth: 24, height: `${Math.max(3, (d.value / max) * 100)}%`, bgcolor: color,
                borderRadius: '4px 4px 0 0', opacity: hover === null || hover === i ? 1 : 0.45, transition: 'opacity 120ms' }} />
            )}
          </Box>
        ))}
      </Box>
      {hover !== null && (
        <Box sx={{ position: 'absolute', top: -6, left: `${((hover + 0.5) / data.length) * 100}%`, transform: 'translate(-50%, -100%)',
          pointerEvents: 'none', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1,
          px: 1, py: 0.5, boxShadow: 2, whiteSpace: 'nowrap', zIndex: 1 }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{data[hover].label}</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{data[hover].tip}</Typography>
        </Box>
      )}
      {axis && (
        <Box display="flex" justifyContent="space-between" mt={0.5}>
          {axis.map((a) => <Typography key={a} sx={{ fontSize: 11.5, color: 'text.secondary' }}>{a}</Typography>)}
        </Box>
      )}
      <Box sx={visuallyHidden}>{/* data for screen readers; a div, because a table can't be squeezed to 1px */}
        <table>
        <caption>{label}</caption>
        <tbody>{data.map((d) => <tr key={d.key}><th>{d.label}</th><td>{d.value}</td></tr>)}</tbody>
      </table>
      </Box>
    </Box>
  );
}

function weekLabel(weeksAgo) {
  const d = new Date();
  d.setDate(d.getDate() - weeksAgo * 7 - ((d.getDay() + 6) % 7));
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export default function MyStatsTab({ onCompleteProfile, profileUrl, userId, isOwn = true, firstName }) {
  const theme = useTheme();
  const color = theme.palette.primary.main;
  const url = isOwn ? '/users/me/stats/' : (userId ? `/users/${userId}/stats/` : null);
  const { data, error, mutate } = useSWR(url, fetcher, { revalidateOnFocus: false });
  const [copied, setCopied] = useState(false);

  if (error) return <Typography color="text.secondary" sx={{ p: 3 }}>The stats could not be loaded right now.</Typography>;
  if (!data) return <Box p={4} textAlign="center"><CircularProgress size={28} /></Box>;

  // One shape for both views: own data has everything, a visitor's only the shared cards
  const shown = new Set(data.public || []);
  const d = isOwn ? {
    visits: { views_30d: data.reach.views_30d, views_prev_30d: data.reach.views_prev_30d },
    followers: { followers: data.reach.followers, followers_30d: data.reach.followers_30d, following: data.reach.following },
    saves: { saves_30d: data.collaboration.saves_30d, saves_prev_30d: data.collaboration.saves_prev_30d },
    visits_chart: { viewers_by_week: data.reach.viewers_by_week },
    together: data.collaboration, record: data.record, writing: data.writing_weeks, writing_score: data.writing_score,
  } : data;
  const has = (k) => Boolean(d[k]) && (isOwn || shown.has(k));
  const toggle = isOwn ? async (key) => {
    const next = shown.has(key) ? [...shown].filter((k) => k !== key) : [...shown, key];
    mutate({ ...data, public: next }, false);
    try { await axiosService.patch('/users/me/stats/', { public: next }); } finally { mutate(); }
  } : null;
  const share = (key) => <ShareToggle shared={shown.has(key)} onToggle={toggle ? () => toggle(key) : null} />;

  const weeks = (d.visits_chart?.viewers_by_week || []).map((v, i, all) => {
    const ago = all.length - 1 - i;
    return { key: i, value: v, label: ago === 0 ? 'This week' : `Week of ${weekLabel(ago)}`, tip: `${v} visit${v === 1 ? '' : 's'}` };
  });
  const totalViews = weeks.reduce((a, w) => a + w.value, 0);
  const years = (d.record?.by_year || []).map((y) => ({ key: y.year, value: y.count, label: String(y.year), tip: `${y.count} publication${y.count === 1 ? '' : 's'}` }));
  const since = data.member_since ? new Date(data.member_since).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : '';
  const tiles = ['visits', 'followers', 'saves'].filter(has);
  const co = d.together;
  const op = isOwn ? data.opportunities : null;
  const profile = isOwn ? data.profile : null;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(profileUrl || window.location.href); setCopied(true); } catch { /* ignore */ }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography sx={{ fontSize: 24, fontWeight: 700 }}>
        {isOwn ? 'Your activity stats' : `${firstName ? `${firstName}’s` : 'Their'} activity stats`}
      </Typography>
      <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5, mb: 3 }}>
        {since ? `On Otable since ${since}` : 'On Otable'}
        {isOwn && ' · the top cards cover the last 30 days'}
        {isOwn && (shown.size
          ? ` · ${shown.size} card${shown.size === 1 ? '' : 's'} shown on your profile (the eye in its corner)`
          : ' · only you see this page; the eye on a card shows it on your profile')}
      </Typography>

      {tiles.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${tiles.length}, 1fr)` }, gap: 2, mb: 2 }}>
          {has('visits') && (
            <Tile label="Profile visits" value={n(d.visits.views_30d)}>
              <Delta now={d.visits.views_30d} before={d.visits.views_prev_30d} />
            </Tile>
          )}
          {has('followers') && (
            <Tile label="Connections" value={n(d.followers.followers)}>
              <Typography sx={{ fontSize: 13, color: d.followers.followers_30d ? 'success.main' : 'text.secondary', mt: 0.5 }}>
                {d.followers.followers_30d ? `+${d.followers.followers_30d} new this month`
                  : 'none new this month'}
              </Typography>
            </Tile>
          )}
          {has('saves') && (
            <Tile label="Writer saves" value={n(d.saves.saves_30d)}>
              <Delta now={d.saves.saves_30d} before={d.saves.saves_prev_30d} />
            </Tile>
          )}
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 2 }}>
        {has('visits_chart') && (
          <Card title="Profile visits, week by week"
            subtitle={totalViews ? `${n(totalViews)} visits from other people in the last 12 weeks.`
              : isOwn ? 'Nobody has visited yet. Your profile is easiest to find when you share it.' : 'No visits recorded yet.'}
            action={isOwn && !totalViews && <Button size="small" onClick={copyLink}>{copied ? 'Link copied' : 'Copy profile link'}</Button>}>
            <Columns data={weeks} color={color} label="Profile visits per week, last 12 weeks" axis={[weekLabel(11), 'This week']} />
          </Card>
        )}

        {has('together') && (
          <Card title="Working together" action={share('together')}>
            {[
              [co.consortia, co.consortia === 1 ? 'consortium' : 'consortia', isOwn ? '/consortium/' : null],
              [co.coauthors, isOwn ? 'people you work with in them' : 'people in these consortia', null],
              [co.labs, co.labs === 1 ? 'lab' : 'labs', null],
              [co.documents, isOwn ? (co.documents === 1 ? 'proposal or paper you write in' : 'proposals and papers you write in')
                : (co.documents === 1 ? 'proposal or paper in progress' : 'proposals and papers in progress'), null],
            ].map(([value, text, to]) => (
              <Box key={text} display="flex" alignItems="baseline" gap={1.25} py={0.9}
                sx={{ borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { borderTop: 0, pt: 0 } }}>
                <Typography sx={{ fontSize: 22, fontWeight: 700, minWidth: 48, fontVariantNumeric: 'tabular-nums' }}>{n(value)}</Typography>
                <Typography sx={{ fontSize: 14, color: 'text.secondary', flex: 1 }}>{text}</Typography>
                {to && <Button size="small" component={RouterLink} to={to} endIcon={<GoIcon />}>Open</Button>}
              </Box>
            ))}
          </Card>
        )}

        {has('record') && (
          <Card title={isOwn ? 'Your research record' : 'Research record'} action={share('record')}
            subtitle={d.record.publications
              ? `${n(d.record.publications)} publications · ${n(d.record.citations)} citations${d.record.h_index ? ` · h-index ${d.record.h_index}` : ''}`
              : isOwn ? 'Add your publications to your profile and they will show here, year by year.' : 'No publications listed yet.'}>
            {d.record.publications > 0 && (
              <Columns data={years} color={color} height={90} label="Publications per year, last 10 years"
                axis={[years[0]?.label, years[years.length - 1]?.label]} />
            )}
          </Card>
        )}

        {isOwn && op && (
          <Card title="Waiting for you"
            subtitle={op.calls_fit
              ? `${n(op.calls_fit)} open or upcoming funding calls fit your research areas and skills${op.calls_closing_30d ? `; ${n(op.calls_closing_30d)} of them close within 30 days` : ''}.`
              : 'Add research areas and skills to your profile, and calls that fit you will appear here.'}
            action={<Button size="small" component={RouterLink} to="/funding/" endIcon={<GoIcon />}>See calls</Button>}>
            {profile.score < 100 && profile.next_step && (
              <Box sx={{ mt: 0.5, p: 1.75, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Your profile is {profile.score}% complete. One step that helps most:</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 600, mt: 0.5 }}>{profile.next_step.label}</Typography>
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: 0.25 }}>{profile.next_step.why}</Typography>
                {onCompleteProfile && (
                  <Button size="small" sx={{ mt: 1, px: 0 }} onClick={onCompleteProfile}>Complete your profile</Button>
                )}
              </Box>
            )}
          </Card>
        )}
      </Box>

      {has('writing') && (
        <WritingHeatmap weeks={d.writing || []} summary={d.writing_score} isOwn={isOwn} action={share('writing')} />
      )}
    </Box>
  );
}
